import { CSSProperties } from "react";

export interface HairlineStatCell {
  value: string;
  label: string;
}

interface HairlineStatGridProps {
  cells: HairlineStatCell[];
  className?: string;
  style?: CSSProperties;
}

/**
 * HairlineStatGrid, flat 4-column stat row (2-col on mobile).
 *
 * - Row: 1px top + bottom border at hsl(var(--border) / 0.5).
 * - Cells: 1px left border (skipped on the first cell of each row).
 * - Padding: 24px vertical, 32px horizontal.
 * - Value: 48px / 500 / mono / --foreground.
 * - Label: 13px / regular / --foreground @ 0.6, 8px above-gap.
 *
 * Pure presentation, no card chrome, no shadow, no radius.
 */
export const HairlineStatGrid = ({
  cells,
  className,
  style,
}: HairlineStatGridProps) => {
  const hairline = "1px solid hsl(var(--border) / 0.5)";

  return (
    <div
      className={`grid grid-cols-2 md:grid-cols-4 ${className ?? ""}`}
      style={{
        borderTop: hairline,
        borderBottom: hairline,
        ...style,
      }}
    >
      {cells.map((cell, i) => {
        // First cell of each row gets no left border:
        //   - mobile (2-col): index % 2 === 0
        //   - desktop (4-col): index % 4 === 0
        // We approximate with CSS using nth-child so it adapts at the
        // breakpoint without JS, using inline style for the desktop rule
        // and a Tailwind class for the mobile rule.
        return (
          <div
            key={`${cell.label}-${i}`}
            className={[
              "flex flex-col",
              // mobile: remove left border on every odd-index (1st of 2-col row)
              i % 2 === 0 ? "border-l-0" : "",
              // desktop: re-apply / remove based on 4-col rhythm
              i % 4 === 0 ? "md:border-l-0" : "md:border-l",
            ].join(" ")}
            style={{
              padding: "24px 32px",
              borderLeft: i % 2 === 0 ? "none" : hairline,
              // desktop overrides happen via the className above; we set the
              // border color via CSS var so md:border-l picks up tokens.
              borderColor: "hsl(var(--border) / 0.5)",
            }}
          >
            <span
              className="font-mono"
              style={{
                fontSize: 48,
                fontWeight: 500,
                lineHeight: 1.05,
                fontVariantNumeric: "tabular-nums",
                color: "hsl(var(--foreground))",
                letterSpacing: "-0.02em",
              }}
            >
              {cell.value}
            </span>
            <span
              style={{
                marginTop: 8,
                fontSize: 13,
                fontWeight: 400,
                color: "hsl(var(--foreground) / 0.6)",
              }}
            >
              {cell.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default HairlineStatGrid;
